/**
 * Configuration file that maps page patterns to their static components.
 * This allows pages to be accessible at ANY path controlled by the navigation API.
 * 
 * IMPORTANT: Uses pattern matching, NOT exact paths.
 * Example: Pattern "commissioners" will match ANY path ending with "commissioners":
 *          - /management/commissioners
 *          - /about-us/management/commissioners
 *          - /governance/board/commissioners
 *          - /any-path/commissioners
 * 
 * When a pattern matches:
 * 1. The component is imported and rendered directly
 * 2. useDynamicContent hook is NOT called (no API request)
 * 3. The static page logic runs instead
 * 
 * Usage:
 * - Use the last segment of the URL (e.g., "commissioners", "directors")
 * - The same component will render regardless of the full path
 * - This allows the navigation API to change paths without frontend changes
 */

import { ComponentType } from 'react';

export interface StaticPageConfig {
  pattern: string[]; // Patterns to match (e.g., ['commissioners', 'komisaris'])
  matchType: 'exact' | 'endsWith' | 'contains'; // How to match the pattern
  component: () => Promise<{ default: ComponentType }>;
}

export const STATIC_PAGES: StaticPageConfig[] = [
  {
    pattern: ['commissioners', 'komisaris'],
    matchType: 'endsWith', // Matches: /management/commissioners, /manajemen/komisaris, etc.
    component: () => import('../about-us/management/commissioners/page')
  },
  {
    pattern: ['directors', 'direksi'],
    matchType: 'endsWith', // Matches any path ending with "directors"
    component: () => import('../about-us/management/directors/page')
  },
  {
    pattern: ['sharia-supervisory-board', 'dewan-pengawas-syariah'],
    matchType: 'endsWith', // Matches any path ending with "sharia-supervisory-board"
    component: () => import('../about-us/management/sharia-supervisory-board/page')
  },
  {
    pattern: ['corporate-secretary', 'sekretaris-perusahaan'],
    matchType: 'endsWith', // Matches any path ending with "corporate-secretary"
    component: () => import('../about-us/corporate-secretary/page')
  },
  {
    pattern: ['policies', 'kebijakan'],
    matchType: 'endsWith',
    component: () => import('../about-us/governance/policies/page')
  },
  {
    pattern: ['certifications-awards', 'sertifikasi-penghargaan'],
    matchType: 'endsWith',
    component: () => import('../about-us/certifications-awards/page')
  },
  {
    pattern: ['list-of-planned-procurement', 'daftar-perencanaan'],
    matchType: 'endsWith',
    component: () => import('../procurement-information/list-of-planned-procurement/page')
  },
  {
    pattern: ['tender-information', 'informasi-tender'],
    matchType: 'endsWith',
    component: () => import('../procurement-information/tender-information/page')
  },
  {
    pattern: ['annual-financial-report', 'laporan-tahunan-keuangan'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/financial-reports/annual-financial-report/page')
  },
  {
    pattern: ['interim-financial-report', 'laporan-keuangan-interim'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/financial-reports/interim-financial-report/page')
  },
  {
    pattern: ['annual-reports', 'laporan-tahunan'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/annual-reports/page')
  },
  {
    pattern: ['eba-sp-performance', 'kinerja-eba-sp'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/eba-sp-performance/page')
  },
  {
    pattern: ['social-bond-and-sukuk-report', 'obligasi-sosial'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/social-bond-and-sukuk-report/page')
  },
  {
    pattern: ['corporate-ratings', 'peringkat-korporasi'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/corporate-ratings/page')
  },
  {
    pattern: ['investor-update'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/important-info/investor-update/page')
  },
  {
    pattern: ['public-offering-announcement', 'pengumuman-penawaran-umum'],
    matchType: 'endsWith',
    component: () => import('../investor-relations/important-info/public-offering-announcement/page')
  },
  {
    pattern: ['news-activities', 'berita'],
    matchType: 'endsWith',
    component: () => import('../publications/news-activities/page')
  },
  {
    pattern: ['biweekly-economic-and-market-update', 'laporan-ekonomi-dan-pasar-dwimingguan'],
    matchType: 'endsWith',
    component: () => import('../publications/biweekly-economic-and-market-update/page')
  },
  {
    pattern: ['emagazine'],
    matchType: 'endsWith',
    component: () => import('../publications/emagazine/page')
  },
  {
    pattern: ['general-notification', 'pengumuman-umum'],
    matchType: 'endsWith',
    component: () => import('../publications/general-notification/page')
  },
  {
    pattern: ['training-schedule', 'pelatihan-jadwal'],
    matchType: 'endsWith',
    component: () => import('../services/training/training-schedule/page')
  },
  // {
  //   pattern: ['corporate-social-responsibilities'],
  //   matchType: 'contains', // Matches any path containing "annual-reports"
  //   component: () => import('../sustainability/corporate-social-responsibilitie/page')
  // }
];

/**
 * Check if a given slug path has a static page implementation
 */
export function findStaticPage(slug: string[]): StaticPageConfig | undefined {
  const fullPath = slug.join('/');
  const lastSegment = slug[slug.length - 1];
  
  return STATIC_PAGES.find(page => {
    return page.pattern.some(pattern => {
      switch (page.matchType) {
        case 'exact':
          return fullPath === pattern;
        case 'endsWith':
          return lastSegment === pattern;
        case 'contains':
          return fullPath.includes(pattern);
        default:
          return false;
      }
    });
  });
}

/**
 * Check if any static page pattern matches the given slug
 * Useful for nested routes
 */
export function hasStaticPagePrefix(slug: string[]): boolean {
  const fullPath = slug.join('/');
  return STATIC_PAGES.some(page => 
    page.pattern.some(pattern => fullPath.includes(pattern))
  );
}
